import { Box } from '@chakra-ui/react';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { VariantBranchSupplier } from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { LoadingOverlayV2 } from '@texas/components/shared/LoadingOverlayV2';
import { useRegexSearch } from '@texas/components/shared/hooks/useRegexSearch';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SuppliersList } from './SuppliersList';

export function AllSuppliers({
  variantId,
  branchId,
  branchName,
  branchIdentifier,
  onAdd,
  isPdVariant,
  setIsAddingToBranch,
}: {
  variantId: number;
  branchId: number;
  branchName: string;
  branchIdentifier?: string;
  onAdd?: (data: VariantBranchSupplier) => void;
  isPdVariant: boolean;
  setIsAddingToBranch?: (id: number | null) => void;
}) {
  const {
    data: AllSuppliers,
    refetch,
    loading,
    set,
  } = useApiResource(suppliersApi.variant.getAllBranchSuppliers);

  const { t } = useTranslation();
  const { search, setSearch, regexSearch } = useRegexSearch();

  useEffect(() => {
    refetch(variantId, branchId);
  }, [branchId, refetch, variantId]);

  const handleAdded = useCallback(
    (data: VariantBranchSupplier) => {
      set(
        (suppliers) =>
          suppliers?.map((supplier) =>
            supplier.id === data.supplierId
              ? { ...supplier, alreadyAdded: true }
              : supplier,
          ) ?? null,
      );
      onAdd?.(data);
    },
    [onAdd, set],
  );

  return (
    <LoadingOverlayV2 loaded={!loading} label={t('general.loading')}>
      <Box
        pos="sticky"
        top={0}
        zIndex={1}
        bg="texas.bg.900"
        _light={{ bg: 'white' }}
        pt={2}
      >
        <SearchFilterInput
          value={search}
          onChange={(searchTerm) => setSearch(searchTerm)}
          debounceDelay={0}
          placeholder={t('suppliers.filterSuppliers')}
        />
      </Box>

      <SuppliersList
        isPdVariant={isPdVariant}
        variantId={variantId}
        branchId={branchId}
        branchName={branchName}
        branchIdentifier={branchIdentifier}
        setIsAddingToBranch={setIsAddingToBranch}
        suppliers={
          AllSuppliers?.filter((supplier) =>
            supplier.name.toLowerCase().match(regexSearch),
          ) ?? []
        }
        onAdded={handleAdded}
      />
    </LoadingOverlayV2>
  );
}
